import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Home, Users, Receipt, Wrench, AlertTriangle, FolderOpen, LogOut, AlertOctagon, Settings, DollarSign, BarChart3, NotebookPen, UsersRound, Mail, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const SIDEBAR_TEXTURE =
  "https://images.unsplash.com/photo-1558346648-9757f2fa4474?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxtYXJibGUlMjBjb3VudGVyfGVufDB8fHx8MTc4MTA2NDMxOHww&ixlib=rb-4.1.0&q=85";

const ADMIN_ROLES = ["super_admin", "landlord"];

const staffNavBase = [
  { to: "/landlord", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/landlord/properties", icon: Home, label: "Properties" },
  { to: "/landlord/tenants", icon: Users, label: "Tenants" },
  { to: "/landlord/rents", icon: Receipt, label: "Rents" },
  { to: "/landlord/overdue", icon: AlertOctagon, label: "Overdue" },
  { to: "/landlord/financials", icon: DollarSign, label: "Financials" },
  { to: "/landlord/pnl", icon: BarChart3, label: "P&L", testid: "nav-pnl" },
  { to: "/landlord/maintenance", icon: Wrench, label: "Maintenance" },
  { to: "/landlord/incidents", icon: AlertTriangle, label: "Incidents" },
  { to: "/landlord/documents", icon: FolderOpen, label: "Documents" },
  { to: "/landlord/notes", icon: NotebookPen, label: "Notes" },
  { to: "/landlord/letters", icon: Mail, label: "Letters", testid: "nav-letters" },
];

const adminOnlyNav = [
  { to: "/landlord/team", icon: UsersRound, label: "Team", testid: "nav-team" },
  { to: "/landlord/ai", icon: Sparkles, label: "AI Assistant", testid: "nav-ai" },
  { to: "/landlord/security", icon: ShieldCheck, label: "Security", testid: "nav-security" },
  { to: "/landlord/settings", icon: Settings, label: "Settings" },
];

const tenantNav = [
  { to: "/tenant", icon: LayoutDashboard, label: "My Home", end: true },
  { to: "/tenant/maintenance", icon: Wrench, label: "Maintenance" },
  { to: "/tenant/incidents", icon: AlertTriangle, label: "Incidents" },
  { to: "/tenant/rents", icon: Receipt, label: "Rent History" },
];

const ROLE_LABEL = {
  super_admin: "Super Admin",
  landlord: "Landlord",
  employee: "Employee",
  tenant: "Tenant",
};

export default function AppLayout({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  let nav;
  if (user?.role === "tenant") nav = tenantNav;
  else {
    nav = [...staffNavBase];
    if (ADMIN_ROLES.includes(user?.role)) nav = nav.concat(adminOnlyNav);
  }

  const initials = (user?.name || user?.username || "U").split(/[.\s]/).map((n) => n[0]).slice(0, 2).join("").toUpperCase();
  const roleLabel = ROLE_LABEL[user?.role] || user?.role;

  const onLogout = async () => {
    await logout();
    navigate("/auth");
  };

  return (
    <div className="min-h-screen flex bg-ivory text-ink" data-testid="app-shell">
      {/* Sidebar — Ink black with gold accents */}
      <aside className="w-64 bg-ink text-ivory flex flex-col relative overflow-hidden" data-testid="sidebar">
        {/* Brand */}
        <div className="px-6 h-20 flex items-center gap-3 border-b border-white/10">
          <img src="/logo.jpg" alt="RojasProperty" className="h-10 w-10 rounded-full object-cover ring-1 ring-gold/60" />
          <div>
            <div className="font-display text-base tracking-tight leading-none">RojasProperty</div>
            <div className="text-[9px] tracking-[0.3em] text-gold mt-1.5 uppercase font-semibold">Private Estates</div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          <div className="px-3 pt-3 pb-2 micro-label text-gold/80">
            {ROLE_LABEL[user?.role] || (user?.role === "tenant" ? "Tenant" : "Workspace")}
          </div>
          {nav.map(({ to, icon: Icon, label, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid || `nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
              className={({ isActive }) =>
                `group relative flex items-center gap-3 px-3 py-2.5 text-sm transition-all duration-300 ${
                  isActive
                    ? "bg-inkSoft text-ivory"
                    : "text-ivory/65 hover:bg-inkSoft/70 hover:text-ivory"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span className="absolute left-0 top-1.5 bottom-1.5 w-[2px] bg-gold" />
                  )}
                  <Icon className="h-4 w-4" strokeWidth={1.5} />
                  <span className={`tracking-wide ${isActive ? "font-medium" : ""}`}>{label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Marble texture strip + user block */}
        <div className="relative">
          <div
            className="absolute inset-0 opacity-20 pointer-events-none"
            style={{ backgroundImage: `url(${SIDEBAR_TEXTURE})`, backgroundSize: "cover", backgroundPosition: "center" }}
          />
          <div className="relative p-4 border-t border-white/10 backdrop-blur-[2px] bg-ink/70">
            <div className="flex items-center gap-3 px-1 py-2">
              <Avatar className="h-9 w-9 ring-1 ring-gold/40">
                <AvatarFallback className="bg-gold text-ink text-xs font-semibold">{initials}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate text-ivory" data-testid="user-name">{user?.name}</div>
                <div className="text-[10px] text-gold/80 truncate tracking-wider" data-testid="user-username">@{user?.username || "—"}</div>
                <div className="text-[9px] text-ivory/45 truncate tracking-[0.2em] uppercase mt-0.5">{roleLabel}</div>
              </div>
            </div>
            <Button
              data-testid="logout-button"
              onClick={onLogout}
              variant="ghost"
              className="w-full justify-start gap-2 text-ivory/60 hover:bg-inkSoft hover:text-gold mt-1 rounded-none uppercase tracking-[0.2em] text-[10px] font-semibold"
              size="sm"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0 bg-ivory">
        <header
          className="h-20 bg-white border-b border-bone px-10 flex items-center justify-between"
          data-testid="page-header"
        >
          <div>
            <div className="micro-label text-gold mb-1">{user?.role === "tenant" ? "Resident" : "The Workspace"}</div>
            <h1 className="font-display text-2xl tracking-tight text-ink">{title}</h1>
            {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">{actions}</div>
        </header>
        <div className="flex-1 p-10 animate-fade-up">{children}</div>
      </main>
    </div>
  );
}
